/* global TextDecoder */

import { apiFetch, apiFetchRaw } from '../../api/utils/api';
import {
  ACTION_ERROR_CODES,
  createActionResultError,
  createActionResultOk,
  isAISendThreadMessageResponse,
  isAIThreadListResponse,
  isAIThreadMessagesResponse,
  isAIThreadResponse,
} from '@formue/ai-contract';

const THREADS_BASE_URL = '/v3/chat/threads';
const STREAM_EVENT_TYPES = Object.freeze({
  STARTED: 'started',
  HEARTBEAT: 'heartbeat',
  ASSISTANT_TEXT: 'assistant_text',
  TOOL_CALL_STARTED: 'tool_call_started',
  TOOL_CALL_FINISHED: 'tool_call_finished',
  FINAL: 'final',
  ERROR: 'error',
});

const isObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

class StreamSchemaError extends Error {
  constructor(message, payload) {
    super(message);
    this.name = 'StreamSchemaError';
    this.payload = payload;
  }
}

class StreamStopError extends Error {
  constructor() {
    super('Stream stopped after error event.');
    this.name = 'StreamStopError';
  }
}

const toApiErrorResult = (error, defaultMessage) =>
  createActionResultError(
    ACTION_ERROR_CODES.CHAT_SERVER_UNREACHABLE,
    `${defaultMessage}${error?.status ? ` (HTTP ${error.status})` : ''}.`,
    {
      message: error?.message,
      status: error?.status,
    }
  );

const toInvalidSchemaResult = (message, payload) =>
  createActionResultError(
    ACTION_ERROR_CODES.CHAT_RESPONSE_INVALID,
    message,
    payload
  );

const parseErrorBody = async (error) => {
  if (!error?.response || typeof error.response.json !== 'function') {
    return null;
  }

  try {
    return await error.response.json();
  } catch {
    return null;
  }
};

const isStartedEvent = (event) =>
  isObject(event) &&
  event.type === STREAM_EVENT_TYPES.STARTED &&
  typeof event.request_id === 'string' &&
  typeof event.thread_id === 'string' &&
  typeof event.started_at === 'string';

const isHeartbeatEvent = (event) =>
  isObject(event) &&
  event.type === STREAM_EVENT_TYPES.HEARTBEAT &&
  typeof event.ts === 'string';

const isAssistantTextEvent = (event) =>
  isObject(event) &&
  event.type === STREAM_EVENT_TYPES.ASSISTANT_TEXT &&
  typeof event.text === 'string';

const isToolCallEvent = (event, expectedType) =>
  isObject(event) &&
  event.type === expectedType &&
  Number.isFinite(event.round) &&
  typeof event.tool_name === 'string' &&
  typeof event.tool_call_id === 'string';

const isFinalEvent = (event) =>
  isObject(event) &&
  event.type === STREAM_EVENT_TYPES.FINAL &&
  (event.status_code === 200 || event.status_code === 502) &&
  isObject(event.payload);

const isErrorEvent = (event) =>
  isObject(event) &&
  event.type === STREAM_EVENT_TYPES.ERROR &&
  isObject(event.error) &&
  typeof event.error.code === 'string' &&
  typeof event.error.message === 'string' &&
  typeof event.error.retryable === 'boolean';

const parseStreamEventLine = (line) => {
  let event;
  try {
    event = JSON.parse(line);
  } catch (error) {
    throw new StreamSchemaError('Send message stream contained invalid JSON.', {
      line,
      message: error?.message,
    });
  }

  const eventType = event?.type;
  switch (eventType) {
    case STREAM_EVENT_TYPES.STARTED:
      if (!isStartedEvent(event)) {
        throw new StreamSchemaError(
          'Send message stream "started" event did not match expected schema.',
          event
        );
      }
      return event;
    case STREAM_EVENT_TYPES.HEARTBEAT:
      if (!isHeartbeatEvent(event)) {
        throw new StreamSchemaError(
          'Send message stream "heartbeat" event did not match expected schema.',
          event
        );
      }
      return event;
    case STREAM_EVENT_TYPES.ASSISTANT_TEXT:
      if (!isAssistantTextEvent(event)) {
        throw new StreamSchemaError(
          'Send message stream "assistant_text" event did not match expected schema.',
          event
        );
      }
      return event;
    case STREAM_EVENT_TYPES.TOOL_CALL_STARTED:
      if (!isToolCallEvent(event, STREAM_EVENT_TYPES.TOOL_CALL_STARTED)) {
        throw new StreamSchemaError(
          'Send message stream "tool_call_started" event did not match expected schema.',
          event
        );
      }
      return event;
    case STREAM_EVENT_TYPES.TOOL_CALL_FINISHED:
      if (!isToolCallEvent(event, STREAM_EVENT_TYPES.TOOL_CALL_FINISHED)) {
        throw new StreamSchemaError(
          'Send message stream "tool_call_finished" event did not match expected schema.',
          event
        );
      }
      return event;
    case STREAM_EVENT_TYPES.FINAL:
      if (!isFinalEvent(event)) {
        throw new StreamSchemaError(
          'Send message stream "final" event did not match expected schema.',
          event
        );
      }
      return event;
    case STREAM_EVENT_TYPES.ERROR:
      if (!isErrorEvent(event)) {
        throw new StreamSchemaError(
          'Send message stream "error" event did not match expected schema.',
          event
        );
      }
      return event;
    default:
      throw new StreamSchemaError('Send message stream event type is unsupported.', {
        event,
      });
  }
};

const emitStreamEvent = (event, onStreamEvent) => {
  if (typeof onStreamEvent === 'function') {
    onStreamEvent(event);
  }
};

const parseNDJSONStream = async ({ stream, onLine }) => {
  if (!stream || typeof stream.getReader !== 'function') {
    throw new StreamSchemaError(
      'Send message response body was not a readable stream.',
      stream
    );
  }

  if (typeof onLine !== 'function') {
    throw new StreamSchemaError('NDJSON parser requires an onLine handler.');
  }

  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      let newlineIndex = buffer.indexOf('\n');
      while (newlineIndex >= 0) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (line) {
          await onLine(line);
        }

        newlineIndex = buffer.indexOf('\n');
      }
    }

    buffer += decoder.decode();
    const trailingLine = buffer.trim();
    if (trailingLine) {
      await onLine(trailingLine);
    }
  } catch (error) {
    if (typeof reader.cancel === 'function') {
      try {
        await reader.cancel();
      } catch {
        // Ignore cancellation errors.
      }
    }
    throw error;
  } finally {
    if (typeof reader.releaseLock === 'function') {
      reader.releaseLock();
    }
  }
};

const consumeSendMessageStream = async ({ stream, onStreamEvent }) => {
  let finalEvent = null;
  let errorEvent = null;

  try {
    await parseNDJSONStream({
      stream,
      onLine: (line) => {
        const event = parseStreamEventLine(line);
        emitStreamEvent(event, onStreamEvent);

        if (event.type === STREAM_EVENT_TYPES.FINAL) {
          finalEvent = event;
          return;
        }

        if (event.type === STREAM_EVENT_TYPES.ERROR) {
          errorEvent = event;
          throw new StreamStopError();
        }
      },
    });
  } catch (error) {
    if (error instanceof StreamStopError && errorEvent) {
      return { finalEvent, errorEvent };
    }
    throw error;
  }

  return { finalEvent, errorEvent };
};

const fetchValidatedThreadResponse = async ({
  endpoint,
  method,
  body,
  validator,
  schemaErrorMessage,
  requestErrorMessage,
}) => {
  try {
    const { body: responseBody } = await apiFetch(endpoint, {
      method,
      ...(body === undefined ? {} : { body }),
    });

    if (!validator(responseBody)) {
      return toInvalidSchemaResult(schemaErrorMessage, responseBody);
    }

    return createActionResultOk(responseBody);
  } catch (error) {
    return toApiErrorResult(error, requestErrorMessage);
  }
};

export const createAIThread = async ({ title } = {}) =>
  fetchValidatedThreadResponse({
    endpoint: THREADS_BASE_URL,
    method: 'POST',
    body: title ? { title } : {},
    validator: isAIThreadResponse,
    schemaErrorMessage: 'Create thread response did not match expected schema.',
    requestErrorMessage: 'Create thread request failed',
  });

export const listAIThreads = async ({ limit = 20 } = {}) =>
  fetchValidatedThreadResponse({
    endpoint: `${THREADS_BASE_URL}?limit=${limit}`,
    method: 'GET',
    validator: isAIThreadListResponse,
    schemaErrorMessage: 'List threads response did not match expected schema.',
    requestErrorMessage: 'List threads request failed',
  });

export const renameAIThread = async ({ threadId, title }) =>
  fetchValidatedThreadResponse({
    endpoint: `${THREADS_BASE_URL}/${threadId}`,
    method: 'PATCH',
    body: { title },
    validator: isAIThreadResponse,
    schemaErrorMessage: 'Rename thread response did not match expected schema.',
    requestErrorMessage: 'Rename thread request failed',
  });

export const deleteAIThread = async ({ threadId }) => {
  try {
    const { body } = await apiFetch(`${THREADS_BASE_URL}/${threadId}`, {
      method: 'DELETE',
    });

    return createActionResultOk({
      threadId,
      body: body === undefined ? null : body,
    });
  } catch (error) {
    return toApiErrorResult(error, 'Delete thread request failed');
  }
};

export const getAIThreadMessages = async ({ threadId, limit = 100 }) =>
  fetchValidatedThreadResponse({
    endpoint: `${THREADS_BASE_URL}/${threadId}/messages?limit=${limit}`,
    method: 'GET',
    validator: isAIThreadMessagesResponse,
    schemaErrorMessage: 'Thread messages response did not match expected schema.',
    requestErrorMessage: 'Get thread messages request failed',
  });

export const sendAIThreadMessage = async ({
  threadId,
  message,
  context,
  onStreamEvent,
}) => {
  try {
    const { body } = await apiFetchRaw(`${THREADS_BASE_URL}/${threadId}/messages`, {
      method: 'POST',
      body: {
        message,
        context,
      },
    });

    const { finalEvent, errorEvent } = await consumeSendMessageStream({
      stream: body,
      onStreamEvent,
    });

    if (errorEvent) {
      return createActionResultError(
        ACTION_ERROR_CODES.CHAT_SERVER_UNREACHABLE,
        errorEvent.error.message,
        {
          code: errorEvent.error.code,
          retryable: errorEvent.error.retryable,
        }
      );
    }

    if (!finalEvent) {
      return toInvalidSchemaResult(
        'Send message stream did not include a final event.',
        body
      );
    }

    if (!isAISendThreadMessageResponse(finalEvent.payload)) {
      return toInvalidSchemaResult(
        'Send message final payload did not match expected schema.',
        finalEvent.payload
      );
    }

    return createActionResultOk({
      ...finalEvent.payload,
      status_code: finalEvent.status_code,
    });
  } catch (error) {
    if (error instanceof StreamSchemaError) {
      return toInvalidSchemaResult(error.message, error.payload);
    }

    // Some gateways return a structured fallback body with HTTP 502.
    if (error?.status === 502) {
      const errorBody = await parseErrorBody(error);

      if (isAISendThreadMessageResponse(errorBody)) {
        return createActionResultOk(errorBody);
      }

      return toInvalidSchemaResult(
        'Send message 502 response did not match expected schema.',
        errorBody
      );
    }

    return toApiErrorResult(error, 'Send message request failed');
  }
};

export const submitAIBadMessageFeedback = async ({ threadId, messageId }) => {
  try {
    const { body, status } = await apiFetch(
      `${THREADS_BASE_URL}/${threadId}/messages/${messageId}/feedback`,
      {
        method: 'POST',
        body: { rating: 'bad' },
      }
    );

    return createActionResultOk({
      threadId,
      messageId,
      status,
      body: body === undefined ? null : body,
    });
  } catch (error) {
    return toApiErrorResult(error, 'Submit bad message feedback request failed');
  }
};
